<template>
  <BaseButton
    class="submit-button button-with-spinner"
    :disabled="isDisabled"
    :size="size"
    :color="color"
    @click="handleClick"
  >
    <ButtonSpinner v-if="working" color="blue-basic" :size="size" />
    <span style="vertical-align: middle">
      <template v-if="working && textWhileWorking">
        {{ textWhileWorking }}
      </template>
      <template v-else>
        <template v-if="$slots.default">
          <slot />
        </template>
        <template v-else>
          {{ working ? textWhileWorking : text }}
        </template>
      </template>
    </span>
  </BaseButton>
</template>

<script>
import { ColorSchemes, SizeNames } from '@/utils/enum'
import BaseButton from '@/components/BaseButton.vue'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import { validateColorScheme } from '@/utils/propValidation'

/**
 * This component will automatically enter a 'loading' state when clicked
 * unless it is provided with the 'status' prop to control the button state explicitly
 * - Disable the button via the following methods: 1. Provide any :status prop other than 'enabled'
 * 2. Use the native :disabled="true" when using the component
 *
 */
export default {
  components: { BaseButton, ButtonSpinner },
  props: {
    status: {
      type: String,
      default: null,
      validator(value) {
        return ['enabled', 'working', 'disabled'].includes(value)
      },
    },
    size: {
      type: String,
      default: SizeNames.MD,
      validator(value) {
        return Object.values(SizeNames).includes(value)
      },
    },
    color: {
      type: String,
      default: ColorSchemes.DEFAULT,
      validator(value) {
        return validateColorScheme(value)
      },
    },
    text: { type: String, default: $t('Submit') },
    textWhileWorking: { type: String, default: $t('Saving') },
  },
  data() {
    return {
      workingStatusTriggeredInternally: null,
    }
  },
  computed: {
    isDisabled() {
      if (this.$attrs?.disabled) {
        return true
      }
      if (this.hasStatusProp) {
        return this.status !== 'enabled'
      }
      return this.working
    },
    hasStatusProp() {
      return !!this.status
    },
    working() {
      return this.status === 'working' || this.workingStatusTriggeredInternally
    },
    btnSizeClass() {
      return `btn-${this.size}`
    },
  },
  methods: {
    handleClick() {
      // Only control the status internally if no status
      // prop is being provided by the consumer
      if (!this.hasStatusProp) {
        this.workingStatusTriggeredInternally = true
      }
    },
  },
}
</script>

<style scoped lang="scss">
.button-working {
  padding-right: 20px;
}
</style>
